import {
    Autocomplete,
    Backdrop,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormHelperText,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    TextField,
    Typography
} from '@mui/material';
import React, {HTMLInputTypeAttribute, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useTranslation} from "react-i18next";
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import {
    useGetDraftQuery,
    useGetDraftsQuery,
    useGetMediaTreeQuery,
    useGetTagsQuery
} from '../../drafts/api/draftsApiSlice';
import {useDispatch, useSelector} from 'react-redux';
import {initialCreationFormState, selectCreationForm, setCreationForm} from '../store/creationsSlice';
import {ClearIcon, DateTimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {useGetCmEnumsQuery} from '../../../app/appApiSlice';
import {Draft, MediaNode, PropertyAttribute} from '../../drafts/types';
import CheckboxTree from 'react-checkbox-tree';
import useDebounce from '../../../hooks/useDebounce';
import './CreationForm.scss'
import {CreationPayload, CreationProperty, PossibleEvent} from "../types";
import DraftsList from "../../drafts/list/DraftsList";
import {
    useCreateCreationMutation,
    useDeleteCreationMutation,
    useGetCreationDetailsQuery,
    useGetResponsiblePersonsQuery,
    useManageStateMutation,
    usePartialUpdateCreationMutation,
    useUpdateCreationMutation
} from "../api/creationsApiSlice";
import {useSnackbar} from "notistack";
import {BreadCrumb} from "../../mediabank/store/mediabankResourcesSlice";
import {
    useGetMediabankResourcesForSelectQuery,
    useLazyGetMediabankResourceQuery,
    useLazyGetMediabankResourcesBulkQuery
} from "../../mediabank/api/mediabankResourcesApiSlice";
import Link from "@mui/material/Link";
import {MediabankResourceWithPropID, ResourceType} from "../../mediabank/types";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {Thumbnail} from "../../../config/images";
import DialogContentText from "@mui/material/DialogContentText";
import CmCreationStatusIconBox from "../../../components/CmCreationStatusIconBox/CmCreationStatusIconBox";
import OwnershipSelect from "../../shared/ownershipSelect/OwnershipSelect";
import OutlinedInput from "@mui/material/OutlinedInput";
import Chip from "@mui/material/Chip";
import {useTheme} from "@mui/material/styles";
import {getUuid} from "../../../config/general";
import UsedMediaTable from "../../vooh/usedMediaTable/usedMediaTable";
import {ApiTags, baseApi} from "../../../app/api/baseApi";
import {AuthResponse} from "../../auth/types";
import ability from "../../../utils/ability";

interface SelectedMediabankResource {
    propId: number;
    resourceId: number;
    name: string;
    thumbnailUrl: string | null;
    resourceUrl: string | null;
}

const CreationForm = () => {

    const [formErrors, setFormErrors] = useState({
        name: false,
        startAt: false,
        endAt: false,
        media: false,
        customProps: [] as number[]
    })
    const {creationId, draftId} = useParams()
    const [searchParams, setSearchParams] = useSearchParams();
    const mediabankBreadcrumbsInitialState = [{
        dirName: 'Katalog główny',
        resourceId: null
    }]

    const user = JSON.parse(localStorage.getItem('user') || "{}") as AuthResponse || {};
    const {data: responsiblePersons} =
        useGetResponsiblePersonsQuery();
    const {t} = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState<string[]>([]);
    const [openMediabankDialog, setOpenMediabankDialog] = React.useState<number | null | undefined>(null);
    const [selectedMediabankResources, setSelectedMediabankResources] = React.useState<SelectedMediabankResource[]>([]);
    const [breadcrumbsForMediabankDialog, setBreadcrumbsForMediabankDialog] = React.useState<BreadCrumb[]>(mediabankBreadcrumbsInitialState);
    const [parentIdForForMediabankDialog, setParentIdForMediabankDialog] = React.useState<string | null>(null);
    const [openRemoveCreationDialog, setOpenRemoveCreationDialog] = React.useState<boolean>(false);
    const [openUsedMediaTreeModal, setOpenUsedMediaTreeModal] = useState(false);

    const [skipped, setSkipped] = useState(new Set<number>());
    const dispatch = useDispatch();
    const {isLoading: loadingCreationDetails, data: creationDetails, error: creationDetailsError} =
        useGetCreationDetailsQuery(creationId ? creationId : (searchParams.get('copy') || ''), {skip: (!creationId && !searchParams.get('copy'))});

    const creationForm = useSelector(selectCreationForm);

    const creationDisabled = creationDetails && creationDetails?.state !== 'draft' && !(searchParams.get('copy'))

    const {isLoading: loadingDraftDetails, data: draftDetails} =
        useGetDraftQuery(draftId || '', {skip: !draftId});


    const {isLoading: loadingCmEnums, data: cmEnums} =
        useGetCmEnumsQuery();


    const {isLoading: loadingMediaTree, data: mediaTree} =
        useGetMediaTreeQuery((draftDetails?.mediaFormat || creationDetails?.mediaFormat));

    const {isLoading: loadingResourcesForMediabankDialog, data: resourcesDataForMediabankDialog} =
        useGetMediabankResourcesForSelectQuery({
            parentId: parentIdForForMediabankDialog,
            mediaFormat: null
        }, {skip: !openMediabankDialog});

    const [getFile, {data: FileData}] = useLazyGetMediabankResourceQuery()

    const [getResources, {data: resourcesBulkData}] = useLazyGetMediabankResourcesBulkQuery()

    const [createCreation, {isLoading}] = useCreateCreationMutation();

    const [partialUpdateCreation, {isLoading: updatingFillIinParametersStep}] = usePartialUpdateCreationMutation();

    const [updateCreation, {isLoading: updatingCreation}] = useUpdateCreationMutation();

    const [deleteCreation, {isLoading: deletingCreation}] = useDeleteCreationMutation()

    const [manageState, {isLoading: loadingManageStateUpdate}] = useManageStateMutation()

    const {isLoading: loadingDrafts, isError, isSuccess, data: draftsData} =
        useGetDraftsQuery(`items=10&page=1`, {skip: !ability.can('access', 'SimplifiedInterface')});

    const isStepSkipped = (step: number) => {
        return skipped.has(step);
    };
    const {enqueueSnackbar} = useSnackbar();

    const theme = useTheme();
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const [nodes, setNodes] = useState([]);
    const [filteredNodes, setFilteredNodes] = useState([]);

    const [filterNodesText, setFilterNodesText] = useState('')

    const [mediaLength, setMediaLength] = useState<number | null>(null);
    const [checkedNodes, setCheckedNodes] = useState<string[]>([]);
    const [expanded, setExpanded] = useState([]);
    const [openMediaTreeModal, setOpenMediaTreeModal] = useState(false);
    const [openNoteModal, setOpenNoteModal] = useState(false);
    const [noteBody, setNoteBody] = useState('');
    const [openRejectReasonModal, setOpenRejectReasonModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
    const checkboxTreeRef = useRef<any>()
    const [treeState, setTreeState] = useState({key: Math.random()});
    const navigate = useNavigate();

    let flattenNodeValues: string[] = []

    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    function validateFieldsForCompletingCreation() {
        let propErrors: number[] = []
        creationForm.creationPropertiesAttributes.forEach((customProp) => {
            if (creationProperties!.find(creationProp => creationProp.id === customProp.id)?.required && (customProp.value === undefined || customProp.value === null || customProp.value === '')) {
                customProp.id && propErrors.push(customProp.id)
            }
        })
        let errors = {
            name: !creationForm.name,
            endAt: !creationForm.endAt,
            startAt: !creationForm.startAt,
            customProps: propErrors,
            media: creationForm.localisablesMediatablesAttributes.length === 0
        }
        setFormErrors(errors)
        return errors;
    }

    const disableNextButton = (activeStep === 0 && !draftId) || (activeStep === 1 && !creationForm.name) || (activeStep === 2 && !creationForm.id);

    const filterNodes = (filtered: any, node: any) => {
        if (
            // Node's label matches the search string
            node.label.toLocaleLowerCase().match(filterNodesText.toString().toLocaleLowerCase())
        ) {
            filtered.push(node)
            return filtered;
        }

        if (node.children && node.children.length > 0) {
            const children = (node.children || []).reduce(filterNodes, []);
            if (children.length > 0) {
                filtered.push({...node, children});
            }
        }
        return filtered;
    }


    const handleNext = async () => {
        if (activeStep == 1) {
            if ((!creationForm.name || !draftId) && !creationDetails) {
                setFormErrors({...formErrors, name: !creationForm.name});
                return;
            }
            if ((!creationForm.id && draftId) || (searchParams.get('copy') && creationDetails && creationDetails.draftId)) {
                try {
                    const creationPayload: CreationPayload = {
                        name: creationForm.name,
                        draftId: searchParams.get('copy') ? creationDetails!.draftId : parseInt(draftId || ''),
                        draftPropertiesAttributes: creationForm.creationPropertiesAttributes
                    }

                    const result = await createCreation(creationPayload).unwrap()
                    dispatch(setCreationForm({...creationForm, id: result.data.id}))
                } catch (err: any) {
                    if ((err?.data?.errors?.draft as any[])[0] === 'is incomplete') {
                        enqueueSnackbar(`Wystąpił błąd, nie można utworzyć kopii z niekompletnego schematu...`, {variant: 'error'});
                    } else {
                        enqueueSnackbar(`Wystąpił błąd`, {variant: 'error'});
                    }
                }
            }
        }

        if (activeStep === 2) {
            if (creationForm.id) {
                try {
                    await creationId ? updateCreation(creationForm) : partialUpdateCreation(creationForm).unwrap()
                    dispatch(setCreationForm({...initialCreationFormState.creationForm}))
                    setCheckedNodes([])
                    navigate('/creations')
                    enqueueSnackbar(`Pomyślnie ${creationId ? 'zaktualizowano' : 'utworzono'} kreacje`, {variant: 'success'});
                } catch (err: any) {
                    dispatch(setCreationForm({...initialCreationFormState.creationForm}))
                    setCheckedNodes([])
                    enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
                    navigate('/creations')
                }
            }
        }

        let newSkipped = skipped;
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);

        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped(newSkipped);
    };

    let mediaSize = 0


    function buildMediaTreeCheckboxNodes(mediaTreeElements: MediaNode[]): any {
        return mediaTreeElements
            .map((element: MediaNode, index, array) => {
                if (element.children && element.children.length > 0) {
                    let val = element.id + '_' + element.type + getUuid(index)

                    let place = '';

                    if (element.type === 'Media::Agglomeration') {
                        place = '(Aglomeracja)'
                    } else if (element.type === 'Media::City') {
                        place = '(Miasto)'
                    }

                    return {
                        value: val,
                        label: element.name + ' ' + `${place}`,
                        children: buildMediaTreeCheckboxNodes(element.children),
                    }
                } else {
                    const media = draftDetails ? draftDetails?.media : creationDetails!.draftMedia
                    const shouldShowMedia = media.some(media => media.id === element.id)
                    if (shouldShowMedia) {
                        mediaSize += 1;
                    }
                    let val = element.id + '_' + element.type + getUuid(index)
                    flattenNodeValues.push(val)
                    return {
                        showOnTree: shouldShowMedia,
                        value: val,
                        label: element.asId ? element.asId + ' - ' + element.name : element.name,
                    }
                }
            })
    }


    const findMatchingMediaNode: any = (mediaTreeElements: any) => {
        if (mediaTreeElements.children) {
            findMatchingMediaNode(mediaTreeElements.children);
        } else {
            return mediaTreeElements.showOnTree
        }
    }
    const handleCloseNodesModal = () => {
        setOpenMediaTreeModal(false)
    };

    const onFilterNodeChange = (event: any) => {
        setFilterNodesText(event.target.value)
    }

    const onSelectDraft = (draftId: number, reload = false) => {
        dispatch(baseApi.util.invalidateTags([ApiTags.DraftDetails]));
        setTimeout(function () {
            navigate(`/drafts/${draftId}/new-creation`)
        }, 200)
    }

    const {isLoading: loadingTags, data: tagsData} =
        useGetTagsQuery();

    const shouldSkipDraftsStep = ability.can('access', 'SimplifiedInterface') && draftsData?.data.length === 1

    useEffect(() => {
        if (creationDetailsError && (creationDetailsError as { status: number }).status === 404) {
            navigate(`/not-found`)
        }
    }, [creationDetailsError]);


    useEffect(() => {
        const checkedMedia = checkedNodes.filter(el => el !== 'all').map((node: string) => {
            return node.split('_')[0]
        })

        const mediaToAdd = checkedMedia.map((checkedMediaElement) => {
            return {localisableId: checkedMediaElement, localisableType: 'Medium'}
        })
        dispatch(setCreationForm({
            ...creationForm,
            localisablesMediatablesAttributes: [...mediaToAdd as []]
        }));
        if (mediaToAdd.length > 0) {
            setFormErrors({...formErrors, media: false})
        }
    }, [checkedNodes])

    useEffect(() => {
        if (!draftId && !creationId && !searchParams.get('copy')) {
            dispatch(setCreationForm(initialCreationFormState.creationForm))
            setSteps([`${t("creations.steps.step")} 1 - ${t("creations.steps.selectDraft")}`, `${t("creations.steps.step")} 2 - ${t("creations.steps.adjustCreationViewStep.stepTitle")}`, `${t("creations.steps.step")} 3 - ${t("creations.steps.fillInCreationParameters.stepTitle")}`])
            setActiveStep(0)
        }
    }, [draftId, creationId]);

    function navigateToSecondStepForSimplifiedInterface() {
        const draftDetailsId = draftsData?.data[0].id || draftDetails?.id
        if (shouldSkipDraftsStep && draftDetailsId && !draftId && !creationId && !searchParams.get('copy')) {
            onSelectDraft(draftDetailsId)
        }
    }

    useEffect(() => {
        navigateToSecondStepForSimplifiedInterface();
    }, [draftsData])

    useEffect(() => {
        navigateToSecondStepForSimplifiedInterface();
    })

    useEffect(() => () => {
        dispatch(baseApi.util.resetApiState())
    }, []);


    useEffect(() => {
        if (searchParams.get('copy') && !creationDetails) {
            return
        }

        if (mediaTree && (draftDetails || creationDetails)) {
            mediaSize = 0;
            const media = searchParams.get('copy') ? creationDetails!.media : (draftDetails?.media || creationDetails!.media)
            const mediaTreeCheckboxNodes = buildMediaTreeCheckboxNodes(mediaTree).filter(function filterOutNodeElements(checkboxTreeElement: any) {
                    if (checkboxTreeElement.showOnTree) return true

                    if (checkboxTreeElement.children) {
                        return (checkboxTreeElement.children = checkboxTreeElement.children.filter(filterOutNodeElements)).length
                    }
                }
            )
            setTimeout(() => {
                setNodes(mediaTreeCheckboxNodes)
                setFilteredNodes([{
                    label: 'Wszystkie nośniki',
                    value: 'all',
                    children: mediaTreeCheckboxNodes
                }] as any)
                setMediaLength(mediaSize)
            }, 200)

            setCheckedNodes(flattenNodeValues.filter(el => media.some(media => media.id.toString() === el.split('_')[0])))

        }
    }, [mediaTree, draftDetails, creationDetails])


    useEffect(() => {
        if (draftId && draftDetails && !searchParams.get('copy')) {
            dispatch(setCreationForm({
                ...initialCreationFormState.creationForm,
                creationPropertiesAttributes: draftDetails.draftProperties.map((prop: PropertyAttribute) => {
                    let defaultVal;
                    switch (prop.propType) {
                        case 'string': {
                            defaultVal = ''
                            break;
                        }
                        case 'integer': {
                            defaultVal = 0
                            break;
                        }
                        case 'mediabank_resource': {
                            defaultVal = null
                            break;
                        }
                        default: {
                            defaultVal = false
                        }
                    }

                    return {
                        id: prop.id as number,
                        value: defaultVal
                    }
                })
            }));
            setSteps([`${t("creations.steps.step")} 1 - ${t("creations.steps.selectDraft")} (${draftDetails.name})`, `${t("creations.steps.step")} 2 - ${t("creations.steps.adjustCreationViewStep.stepTitle")}`, `${t("creations.steps.step")} 3 - ${t("creations.steps.fillInCreationParameters.stepTitle")}`])
            setActiveStep(1)
        }
    }, [draftDetails])

    useEffect(() => {
        if ((creationId || searchParams.get('copy')) && creationDetails) {
            const {creationProperties, ownerships, comment, ...creationDetailsRest} = creationDetails;
            let calculatedCreationProperties;

            if (searchParams.get('copy') && draftDetails) {
                calculatedCreationProperties = draftDetails.draftProperties.map((prop: PropertyAttribute) => {
                    return {
                        id: prop.id as number,
                        value: creationDetails?.creationProperties?.find(el => el.key === prop.key)?.value
                    }
                })
            } else {
                calculatedCreationProperties = creationDetails.creationProperties.map((el) => {
                    return {
                        id: el.id,
                        value: el.value
                    }
                })
            }
            dispatch(setCreationForm({
                ...creationDetailsRest,
                localisablesMediatablesAttributes: creationDetails.media.map((el) => {
                    return {localisableId: el.id, localisableType: 'Medium'}
                }),
                creationPropertiesAttributes: calculatedCreationProperties,
                ownershipsAttributes: searchParams.get('copy') ? creationDetails.ownerships
                    .filter((ownership) => {
                        const person = responsiblePersons?.find(person => person.email === user.profile.email)
                        return person?.id !== ownership.userId
                    })

                    .map((el) => {
                        return {
                            userId: el.userId,
                            accessLevel: el.accessLevel
                        }
                    }) || [] : creationDetails.ownerships,
                commentAttributes: comment,
                adTagIds: creationDetails.adTags.map(el => el.id)
            }))

            if (comment) {
                setNoteBody(comment.body)
            }

            getResources((creationDetails.creationProperties.filter(prop => prop.propType === 'mediabank_resource' && prop.value)
                .map((el) => {
                    return {resourceId: el.value as number, propId: el.id!}
                }))).unwrap().then((res: MediabankResourceWithPropID[]) => {
                const selectedResources = res.map((el) => {
                    return {
                        propId: el.propId,
                        name: el.name,
                        resourceId: el.id,
                        thumbnailUrl: el.thumbnailUrl,
                        resourceUrl: el.thumbnailUrl
                    }
                })
                setSelectedMediabankResources(selectedResources)
            })


            setSteps([`${t("creations.steps.step")} 1 - ${t("creations.steps.adjustCreationViewStep.stepTitle")}`, `${t("creations.steps.step")} 2 - ${t("creations.steps.fillInCreationParameters.stepTitle")}`])
            setActiveStep(1)
        }
    }, [creationDetails, draftDetails]);

    useEffect(() => () => {
        dispatch(setCreationForm({...initialCreationFormState.creationForm}))
    }, []);


    useDebounce(() => {
            // Reset nodes back to unfiltered state
            if (!filterNodesText) {
                setFilteredNodes([{
                    label: 'Wszystkie nośniki',
                    value: 'all',
                    children: nodes
                }] as any);
                return;
            }
            setFilteredNodes(
                [{
                    label: 'Wszystkie nośniki',
                    value: 'all',
                    children: nodes.reduce(filterNodes, [])
                }] as any)
            checkboxTreeRef.current.onExpandAll()
        }, [filterNodesText], 100
    );

    const deleteCreationMutation = async () => {
        try {
            setOpenRemoveCreationDialog(false);
            const result = await deleteCreation(creationId).unwrap()
            enqueueSnackbar('Pomyślnie usunięto kreacje', {variant: 'success'});
            navigate('/creations')
        } catch (err: any) {
            enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
        }
    }

    const updateNote = async () => {
        if (creationForm.id) {
            try {
                await partialUpdateCreation({commentAttributes: {body: noteBody}, id: creationForm.id})
                enqueueSnackbar(`Pomyślnie zaktualizowano notatkę`, {variant: 'success'});
                setOpenNoteModal(false);
                dispatch(setCreationForm({
                    ...creationForm,
                    commentAttributes: {
                        body: noteBody
                    }
                }));
            } catch (err: any) {
                enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
                setOpenNoteModal(false);
            }
        } else {
            dispatch(setCreationForm({
                ...creationForm,
                commentAttributes: {
                    body: noteBody
                }
            }));
        }
    }

    const handleRemoveCreation = () => {
        deleteCreationMutation();
    };

    const actionButtons = (possibleEvents: PossibleEvent[]) => {
        return <Box sx={{display: 'flex', gap: '20px'}}>
            {possibleEvents.map((event: PossibleEvent) => {
                return <Button size={'small'} onClick={async () => {
                    if (event === 'complete') {
                        let errors = validateFieldsForCompletingCreation()
                        let isFormValid = true;
                        for (const formErrorsKey in errors) {
                            let fieldError = errors[formErrorsKey as keyof typeof errors]
                            if (fieldError && formErrorsKey !== 'customProps') {
                                isFormValid = false
                            }
                        }
                        if (errors.customProps.length > 0) {
                            isFormValid = false;
                        }

                        if (!isFormValid) {
                            if (activeStep === 1 && errors.customProps.length === 0 && !errors.name) {
                                setActiveStep(2)
                            } else if (activeStep === 2 && !errors.startAt && !errors.endAt && !errors.media) {
                                setActiveStep(1)
                            }
                            enqueueSnackbar('Formularz zawiera błędy', {variant: 'error'});
                            return;
                        }
                    }

                    if (event === 'reject') {
                        setOpenRejectReasonModal(true)
                    } else {
                        try {
                            await updateCreation(creationForm)
                            await manageState({
                                id: creationDetails?.id || null,
                                possibleEvent: event
                            }).unwrap()
                            enqueueSnackbar('Pomyślnie zaktualizowano status', {variant: 'success'});
                        } catch (err: any) {
                            enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
                        }
                    }
                }} variant={'contained'}
                               color={event === 'reject' ? 'error' : (event === 'accept' ? 'success' : 'info')}>{t('creations.possibleEvent.' + event)}</Button>
            })}
            {rejectDialog}
        </Box>
    }

    const createTextProp = (creationFormProp: CreationProperty, draftProp: PropertyAttribute, textFieldType: HTMLInputTypeAttribute | undefined) => {
        return (<TextField
            multiline={textFieldType === 'text'}
            disabled={creationDisabled}
            key={draftProp.id}
            value={creationFormProp ? creationFormProp.value : ''}
            type={textFieldType}
            required={draftProp.required}
            error={formErrors.customProps.some(er => er === draftProp.id)}
            label={draftProp.name}
            onChange={(event) => {
                dispatch(setCreationForm({
                    ...creationForm,
                    creationPropertiesAttributes: creationForm.creationPropertiesAttributes.map((prop: CreationProperty) => {
                        if (creationFormProp.id === prop.id) {
                            return {
                                id: prop.id,
                                value: event.target.value
                            }
                        }
                        return prop
                    })
                }));
                setFormErrors({
                    ...formErrors,
                    customProps: formErrors.customProps.filter(id => id !== creationFormProp.id),
                })
            }}
            variant="outlined"
            sx={{mb: 3}}
            fullWidth/>)
    }

    function openLink(url: string) {
        const link = document.createElement("a");
        link.href = url
        link.target = '_blank'
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link)
    }

    function openImageFile(mediabankResource: SelectedMediabankResource) {
        if (mediabankResource.resourceUrl) {
            openLink(mediabankResource.resourceUrl)
        } else {
            getFile(mediabankResource.resourceId).unwrap().then(async (res) => {
                if (res.data.resourceFileUrl) {
                    openLink(res.data.resourceFileUrl);
                }
            })
        }
    };

    const selectDraftStep =
        <Box sx={{paddingTop: '40px'}}>
            {!loadingDraftDetails ? (
                <DraftsList greyOutNotCompleted={true} showHeader={false} showCrudActions={false} selectable={true}
                            onRowClick={(draft) => {
                                if (draft.completed) {
                                    onSelectDraft(draft.id)
                                }
                            }}
                            activeDraftId={draftId ? draftDetails?.id : null}></DraftsList>
            ) : <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>
                <CircularProgress size={100}/>
            </Box>}
        </Box>

    let creationProperties = creationId ? creationDetails?.creationProperties : draftDetails?.draftProperties
    if (searchParams.get('copy')) {
        creationProperties = creationDetails?.creationProperties?.map((el) => {
            const draftProp = draftDetails?.draftProperties.find((draft) => {
                if (draft.propType === el.propType && draft.key === el.key) return true;
            });

            return {
                ...el,
                id: draftProp?.id
            }
        })
    }

    function getSelectedMediaArrayFromTree(entireObj: MediaNode[]) {
        let foundArr: MediaNode[] = [];
        let building = '';
        JSON.stringify(entireObj, (_, nestedValue) => {

            if (nestedValue.type === 'Media::Building') {
                building = nestedValue.name;
            }
            if (nestedValue && checkedNodes.some(el => el.split('_')[0] === nestedValue.id?.toString())) {
                foundArr.push({...nestedValue, building});
            }
            return nestedValue;
        });
        return foundArr;
    };

    const noteDialog =
        <Dialog
            open={openNoteModal}
            onClose={() => {
                setOpenNoteModal(false)
            }}
            fullWidth={true}
            maxWidth="md"
        >

            <DialogTitle id="alert-dialog-title">
                Notatka
            </DialogTitle>
            <DialogContent>
                <Box sx={{paddingY: '10px'}}>
                    <TextField
                        value={noteBody}
                        onChange={(e) => {
                            setNoteBody(e.target.value as string)
                        }}
                        id="outlined-multiline-static"
                        label="Notatka"
                        multiline
                        fullWidth={true}
                        rows={4}
                    /></Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setOpenNoteModal(false)
                    }}>{t('common.cancel')}</Button>
                <Button
                    variant={'contained'} onClick={(event) => {
                    updateNote()
                }}>{t('common.save')}</Button>
            </DialogActions>
        </Dialog>

    const rejectDialog =
        <Dialog
            open={openRejectReasonModal}
            onClose={() => {
                setOpenRejectReasonModal(false)
            }}
            fullWidth={true}
            maxWidth="md"
        >

            <DialogTitle id="alert-dialog-title">
                Odrzucenie kreacji
            </DialogTitle>
            <DialogContent>
                <Box sx={{paddingY: '10px'}}>
                    <TextField
                        value={rejectReason}
                        onChange={(e) => {
                            setRejectReason(e.target.value as string)
                        }}
                        id="outlined-multiline-static"
                        label="Podaj przyczynę odrzucenia kreacji"
                        multiline
                        fullWidth={true}
                        rows={4}
                    /></Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setOpenRejectReasonModal(false)
                    }}>{t('common.cancel')}</Button>
                <Button
                    variant={'contained'} onClick={async () => {
                    try {
                        await updateCreation(creationForm)
                        await manageState({
                            id: creationDetails?.id || null,
                            possibleEvent: 'reject',
                            rejectionReason: rejectReason,
                        }).unwrap()
                        enqueueSnackbar('Pomyślnie zaktualizowano status', {variant: 'success'});
                    } catch (err: any) {
                        enqueueSnackbar('Wystąpił błąd', {variant: 'error'});
                    }
                    setRejectReason('')
                    setOpenRejectReasonModal(false)
                }}>Wyślij</Button>
            </DialogActions>
        </Dialog>


    const adjustCreationViewStep =
        <Box sx={{paddingTop: '40px', maxWidth: '60%'}}>
            {!loadingDraftDetails ? (
                <form autoComplete="off">
                    <TextField
                        disabled={creationDisabled}
                        value={creationForm.name}
                        label={t('creations.steps.adjustCreationViewStep.creationName')}
                        onChange={(event) => {
                            setFormErrors({...formErrors, name: false})
                            dispatch(setCreationForm({
                                ...creationForm,
                                name: event.target.value
                            }));
                        }}
                        required
                        variant="outlined"
                        sx={{mb: 3}}
                        fullWidth
                        error={formErrors.name}/>
                    {(creationForm.creationPropertiesAttributes.length > 0 && creationProperties) && [...creationProperties]
                        .sort((a, b) => a.sequence - b.sequence)
                        .map((draftProp: PropertyAttribute) => {
                                const creationFormProp = creationForm.creationPropertiesAttributes.filter(creationProp => creationProp.id === draftProp.id)[0]
                                let type: HTMLInputTypeAttribute | undefined;
                                let customProp;
                                switch (draftProp.propType) {
                                    case 'string': {
                                        customProp = createTextProp(creationFormProp, draftProp, 'text')
                                        break;
                                    }
                                    case 'integer': {
                                        customProp = createTextProp(creationFormProp, draftProp, 'number')
                                        break;
                                    }
                                    case 'mediabank_resource': {
                                        const selectedMediabankResource = searchParams.get('copy') ? selectedMediabankResources.filter(el => el.resourceId === creationFormProp?.value)[0] : selectedMediabankResources.filter(el => el.propId === creationFormProp.id)[0]
                                        customProp = (<div key={draftProp.id}>
                                            <Typography
                                                sx={{
                                                    color: 'text.primary',
                                                    paddingY: '10px'
                                                }}>{draftProp.name}:</Typography>
                                            <Box
                                                sx={{display: 'flex', alignItems: 'center', paddingY: '10px', gap: '10px'}}>
                                                {
                                                    selectedMediabankResource &&
                                                    (!selectedMediabankResource.thumbnailUrl ? (
                                                            <DescriptionOutlinedIcon
                                                                fontSize="large"/>) : (
                                                            <Box sx={{
                                                                height: Thumbnail.width,
                                                                width: Thumbnail.width,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: Thumbnail.background
                                                            }}><img
                                                                width={Thumbnail.width}
                                                                height={Thumbnail.width}
                                                                src={`${selectedMediabankResource.thumbnailUrl}`}
                                                                alt={selectedMediabankResource.name}
                                                                loading="lazy"/>
                                                            </Box>)
                                                    )
                                                }
                                                {
                                                    selectedMediabankResource && (
                                                        <Link onClick={(event) => {
                                                            event.preventDefault()
                                                            openImageFile(selectedMediabankResource)
                                                        }
                                                        }

                                                              component={'button'}>{selectedMediabankResource.name}</Link>
                                                    )
                                                }
                                                {
                                                    selectedMediabankResource && (
                                                        <IconButton
                                                            disabled={creationDisabled}
                                                            color={'error'} aria-label="delete" onClick={() => {
                                                            setSelectedMediabankResources([...selectedMediabankResources.filter(el => el.propId !== selectedMediabankResource.propId)])
                                                            dispatch(setCreationForm({
                                                                ...creationForm,
                                                                creationPropertiesAttributes: creationForm.creationPropertiesAttributes.map((prop: CreationProperty) => {
                                                                    if (creationFormProp.id === prop.id) {
                                                                        return {
                                                                            id: prop.id,
                                                                            value: null
                                                                        }
                                                                    }
                                                                    return prop
                                                                })
                                                            }));
                                                        }}>
                                                            <ClearIcon/>
                                                        </IconButton>
                                                    )
                                                }
                                            </Box>
                                            <Button variant="contained"
                                                    className={formErrors.customProps.some(er => er === draftProp.id) ? 'error' : undefined}
                                                    disabled={creationDisabled}
                                                    sx={{width: '100%', display: 'block', marginBottom: '15px'}}
                                                    onClick={() => {
                                                        setOpenMediabankDialog(creationFormProp.id)
                                                    }}>{'Wybierz plik..'}</Button>
                                            {formErrors.customProps.some(er => er === draftProp.id) && (<Typography
                                                sx={{marginBottom: '20px'}}
                                                color={'error'}
                                            >Plik jest wymagany...</Typography>)}

                                            {openMediabankDialog && (<Dialog
                                                open={openMediabankDialog === creationFormProp.id}
                                                onClose={() => {
                                                    setOpenMediabankDialog(null)
                                                }}
                                                aria-labelledby="alert-dialog-title"
                                                aria-describedby="alert-dialog-description"
                                            >
                                                <DialogTitle id="alert-dialog-title">
                                                    Wybierz plik mediabanku
                                                </DialogTitle>
                                                <DialogContent>
                                                    <Box sx={{width: 500, bgcolor: 'background.paper'}}>
                                                        <Breadcrumbs aria-label="breadcrumb">
                                                            {breadcrumbsForMediabankDialog.length && breadcrumbsForMediabankDialog.map((breadcrumb, index) => {
                                                                return index !== breadcrumbsForMediabankDialog.length - 1 ? (
                                                                    <Link key={breadcrumb.dirName} underline="hover"
                                                                          color="inherit"
                                                                          onClick={() => {
                                                                              setParentIdForMediabankDialog(breadcrumb.resourceId)
                                                                              setBreadcrumbsForMediabankDialog(breadcrumbsForMediabankDialog.slice(0, index + 1))
                                                                          }} component="button">
                                                                        {breadcrumb.dirName}
                                                                    </Link>
                                                                ) : (<Typography key={breadcrumb.dirName}
                                                                                 sx={{color: 'text.primary'}}>{breadcrumb.dirName}</Typography>)
                                                            })}
                                                        </Breadcrumbs>
                                                        <List>
                                                            {
                                                                (resourcesDataForMediabankDialog) && (
                                                                    resourcesDataForMediabankDialog.data
                                                                        .map((resource) => {
                                                                            return (<ListItem key={resource.id} disablePadding
                                                                                              selected={!!selectedMediabankResources.filter(el => el.resourceId === resource.id && el.propId === openMediabankDialog).length}
                                                                                              sx={{height: '50px'}}>
                                                                                {resource.resourceType === ResourceType.Dir ? (
                                                                                    <ListItemButton onClick={() => {
                                                                                        setParentIdForMediabankDialog(resource.id.toString())
                                                                                        setBreadcrumbsForMediabankDialog([...breadcrumbsForMediabankDialog, {
                                                                                            resourceId: resource.id.toString(),
                                                                                            dirName: resource.name
                                                                                        }])
                                                                                    }}>
                                                                                        <ListItemIcon>
                                                                                            <FolderOutlinedIcon/>
                                                                                        </ListItemIcon>
                                                                                        <ListItemText primary={resource.name}/>
                                                                                    </ListItemButton>) : (
                                                                                    <ListItemButton onClick={() => {
                                                                                        dispatch(setCreationForm({
                                                                                            ...creationForm,
                                                                                            creationPropertiesAttributes: creationForm.creationPropertiesAttributes.map((prop: CreationProperty) => {
                                                                                                if (creationFormProp.id === prop.id) {
                                                                                                    return {
                                                                                                        id: prop.id,
                                                                                                        value: resource.id
                                                                                                    }
                                                                                                }
                                                                                                return prop
                                                                                            })
                                                                                        }));
                                                                                        setSelectedMediabankResources([...selectedMediabankResources.filter(el => el.propId !== creationFormProp.id), {
                                                                                            propId: creationFormProp.id!,
                                                                                            resourceId: resource.id,
                                                                                            name: resource.name,
                                                                                            thumbnailUrl: resource.thumbnailUrl,
                                                                                            resourceUrl: resource.resourceFileUrl
                                                                                        }])
                                                                                        setFormErrors({
                                                                                            ...formErrors,
                                                                                            customProps: formErrors.customProps.filter(id => id !== creationFormProp.id),
                                                                                        })
                                                                                        setOpenMediabankDialog(null)
                                                                                    }}>
                                                                                        <ListItemIcon>
                                                                                            {
                                                                                                !resource.thumbnailUrl ? (
                                                                                                    <DescriptionOutlinedIcon
                                                                                                        fontSize="large"/>) : (
                                                                                                    <Box sx={{
                                                                                                        height: Thumbnail.width,
                                                                                                        width: Thumbnail.width,
                                                                                                        display: 'flex',
                                                                                                        alignItems: 'center',
                                                                                                        justifyContent: 'center',
                                                                                                        background: Thumbnail.background
                                                                                                    }}><img
                                                                                                        width={Thumbnail.width}
                                                                                                        height={Thumbnail.width}
                                                                                                        src={`${resource.thumbnailUrl}`}
                                                                                                        alt={resource.name}
                                                                                                        loading="lazy"/>
                                                                                                    </Box>)
                                                                                            }
                                                                                        </ListItemIcon>
                                                                                        <ListItemText primary={resource.name}/>
                                                                                    </ListItemButton>)}
                                                                            </ListItem>)
                                                                        })
                                                                )
                                                            }

                                                            {(resourcesDataForMediabankDialog && resourcesDataForMediabankDialog.data.length === 0) && (
                                                                <Typography
                                                                    sx={{color: 'text.primary', paddingY: '10px'}}>Brak
                                                                    plików...</Typography>)
                                                            }
                                                        </List>
                                                    </Box>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button onClick={() => {
                                                        setOpenMediabankDialog(null)
                                                    }} autoFocus>
                                                        {t('common.cancel')}
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>)}


                                        </div>)
                                        break;
                                    }
                                    default: {
                                        customProp = (<><FormControlLabel
                                            disabled={creationDisabled}
                                            key={draftProp.id} sx={{paddingBottom: '15px'}}
                                            onChange={(_, checked) => {
                                                dispatch(setCreationForm({
                                                    ...creationForm,
                                                    creationPropertiesAttributes: creationForm.creationPropertiesAttributes.map((prop: CreationProperty) => {
                                                        if (creationFormProp.id === prop.id) {
                                                            return {
                                                                id: prop.id,
                                                                value: checked
                                                            }
                                                        }
                                                        return prop
                                                    })
                                                }));
                                            }} control={<Checkbox/>}
                                            label={draftProp.name}/>

                                            {formErrors.customProps.some(err => err === draftProp.id) && (
                                                <FormHelperText>Pole jest wymagane</FormHelperText>)}
                                        </>)
                                    }
                                }
                                return customProp
                            }
                        )}

                    <FormControl fullWidth sx={{marginBottom: '25px'}}>
                        {tagsData && (<Autocomplete
                            disabled={creationDisabled}
                            multiple
                            id="tags-filled"
                            options={tagsData.map((option) => option.name)}
                            value={creationForm.tagList}
                            onChange={(_, value) => {
                                dispatch(setCreationForm({
                                    ...creationForm,
                                    tagList: value
                                }));
                            }}
                            freeSolo
                            renderTags={(value: readonly string[], getTagProps) =>
                                value.map((option: string, index: number) => {
                                    const {key, ...tagProps} = getTagProps({index});
                                    return (
                                        <Chip sx={{marginY: '10px'}} color={"info"} variant="filled"
                                              label={option}
                                              key={key} {...tagProps} />
                                    );
                                })
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Tagi"
                                    placeholder={tagsData.length === 0 ? 'Brak utworzonych tagów... wpisz nowy i zatwierdź enterem' : 'Wybierz z listy lub wpisz nowy i zatwierdź enterem'}
                                />
                            )}
                        />)}
                    </FormControl>


                    {cmEnums && (<FormControl fullWidth>
                        <InputLabel id="adtag">Ad tagi</InputLabel>
                        <Select
                            disabled={creationDisabled}
                            labelId="adtag"
                            id="demo-multiple-chip"
                            multiple
                            value={creationForm.adTagIds}
                            onChange={(event) => {
                                dispatch(setCreationForm({
                                    ...creationForm,
                                    adTagIds: typeof event.target.value === "number" ? [parseInt(event.target.value)] : event.target.value as any[]
                                }));
                            }}
                            input={<OutlinedInput id="select-multiple-chip" label="Ad tagi"/>}
                            renderValue={(selected) => (
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {selected.map((value) => (
                                        <Chip key={value}
                                              disabled={creationDisabled}
                                              onMouseDown={(event) => {
                                                  event.stopPropagation();
                                              }}
                                              color={"info"} variant="filled"
                                              onDelete={(e) => {
                                                  dispatch(setCreationForm({
                                                      ...creationForm,
                                                      adTagIds: creationForm.adTagIds.filter((adTag) => adTag !== value)
                                                  }));
                                              }}
                                              label={(cmEnums?.find(cmEnum => cmEnum.name === 'adTags')?.values as {
                                                  id: number;
                                                  name: string
                                              }[]).filter(el => el.id === value)[0].name}/>
                                    ))}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            {(cmEnums.find(cmEnum => cmEnum.name === 'adTags')?.values as {
                                id: number;
                                name: string
                            }[]).map((adTag) => (
                                <MenuItem
                                    key={adTag.name}
                                    value={adTag.id}
                                    style={{
                                        fontWeight: theme.typography.fontWeightMedium
                                    }}
                                >
                                    {adTag.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>)}
                </form>
            ) : <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px'}}>
                <CircularProgress size={100}/>
            </Box>}
        </Box>


    const addAuthorizedPerson = (authorizedPerson: number | null, authorizedPersonRights: string) => {
        if (!authorizedPerson) {
            return;
        }

        const existingPerson = creationForm.ownershipsAttributes.find(el => el.userId === authorizedPerson)

        const personToAdd = existingPerson ? {
            userId: authorizedPerson,
            accessLevel: authorizedPersonRights,
            id: existingPerson.id
        } : {
            userId: authorizedPerson,
            accessLevel: authorizedPersonRights
        }

        dispatch(setCreationForm({
            ...creationForm,
            ownershipsAttributes: [...creationForm.ownershipsAttributes.filter(person => person.userId !== authorizedPerson), personToAdd]
        }));
    }

    const getNextButtonTitle = () => {
        if (activeStep === 2 && creationId) {
            return t("creations.updateCreation");
        }
        if (activeStep === 2) {
            return t("creations.createCreation");
        }
        return t("common.next");
    }

    const hideUpdateCreationButton = activeStep === 2 && creationId && creationDetails && creationDetails.state !== 'draft'

    const fillInParametersStep =
        <Box sx={{
            paddingTop: '40px',
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'space-between'
        }}>
            <Box sx={{width: '60%'}}>
                <form autoComplete="off">

                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: '10px', marginX: 'auto'}}>
                        <Button
                            className={formErrors.media ? 'error' : undefined}
                            disabled={mediaTree?.length === 0}
                            variant="contained"
                            fullWidth={true}
                            onClick={() => {
                                setOpenMediaTreeModal(true)
                            }} sx={{marginBottom: '20px', textAlign: 'left', width: '450px'}}>
                            Wybór nośników &nbsp;
                            {(mediaTree?.length === 0) ? <CircularProgress sx={{marginLeft: '10px'}}
                                                                           size={20}/> : `${checkedNodes.length}/${mediaLength || 0}`}</Button>
                        {formErrors.media && (<Typography
                            sx={{marginBottom: '20px'}}
                            color={'error'}
                        >Kreacja musi mieć wybrane nośniki...</Typography>)}

                        <Button disabled={loadingMediaTree} variant="text"
                                onClick={() => {
                                    setOpenUsedMediaTreeModal(true)

                                }} sx={{marginTop: '20px', marginLeft: '20px', width: '450px'}}>Wybrane
                            nośniki {(loadingMediaTree) ?
                                <CircularProgress sx={{marginLeft: '10px'}}
                                                  size={20}/> :
                                <strong>&nbsp;{`${checkedNodes.length}/${mediaLength || 0}`} </strong>
                            }</Button></Box>


                    <Dialog
                        open={openMediaTreeModal}
                        onClose={handleCloseNodesModal}
                        fullWidth={true}
                        maxWidth="md"
                    >

                        <DialogTitle id="alert-dialog-title">
                            {(creationDisabled) ? "Wybrane nośniki" : t("draftForm.chooseMedia")}
                        </DialogTitle>
                        <DialogContent>

                            {(loadingMediaTree) && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '300px'
                                    }}>
                                    <CircularProgress size={100}/>
                                </Box>)}

                            <Box sx={{minHeight: '300px'}}>
                                {mediaTree && (
                                    <><TextField
                                        value={filterNodesText}
                                        label="Wyszukaj..."
                                        onInput={onFilterNodeChange}
                                        variant="outlined"
                                        sx={{mt: 1, mb: 3}}
                                        fullWidth
                                    />
                                        <CheckboxTree
                                            disabled={(creationDisabled)}
                                            ref={checkboxTreeRef}
                                            key={treeState.key}
                                            showNodeTitles={true}
                                            nodes={filteredNodes}
                                            checked={checkedNodes}
                                            expanded={expanded}
                                            showExpandAll={true}
                                            onCheck={(checked: any) => {
                                                setCheckedNodes(checked)
                                                setTreeState({key: Math.random()})
                                            }}
                                            onExpand={(expanded: any) => {
                                                setExpanded(expanded)
                                            }}
                                        /></>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseNodesModal}>{t('common.closeWindow')}</Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openUsedMediaTreeModal}
                        onClose={() => {
                            setOpenUsedMediaTreeModal(false)
                        }}
                        fullWidth={true}
                        maxWidth="md">

                        <DialogTitle id="alert-dialog-title">
                            Używane nośniki
                        </DialogTitle>
                        <DialogContent>

                            {(loadingMediaTree) && (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '300px'
                                }}>
                                    <CircularProgress size={100}/>
                                </Box>)}

                            <Box sx={{minHeight: '300px'}}>
                                {(mediaTree && checkedNodes.length > 0) ? (
                                    <UsedMediaTable
                                        buildingColumnName={'Budynek'}
                                        usedMedia={getSelectedMediaArrayFromTree(mediaTree)}></UsedMediaTable>
                                ) : <p>Brak zaznaczonych nośników...</p>}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => {
                                setOpenUsedMediaTreeModal(false)
                            }}>{t('common.closeWindow')}</Button>
                        </DialogActions>
                    </Dialog>

                    <Box sx={{display: 'flex', gap: '40px', alignItems: 'center'}}>
                        <Box sx={{width: '100%'}}>
                            <DateTimePicker
                                minDate={dayjs(new Date())}
                                maxDate={creationForm.endAt ? dayjs(new Date(creationForm.endAt)) : undefined}
                                disabled={creationDisabled}
                                value={dayjs(creationForm.startAt)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined',
                                        error: formErrors.startAt
                                    },
                                }}
                                label={t('creations.steps.fillInCreationParameters.emissionStart')}
                                onChange={(event) => {
                                    setFormErrors({...formErrors, startAt: false})
                                    dispatch(setCreationForm({
                                        ...creationForm,
                                        startAt: dayjs(event).date() ? dayjs(event).format() : ''
                                    }))
                                }}/></Box>
                        <span>-</span>
                        <Box sx={{width: '100%'}}>
                            <DateTimePicker
                                minDate={creationForm.startAt ? dayjs(creationForm.startAt) : dayjs(new Date())}
                                disabled={creationDisabled}
                                value={dayjs(creationForm.endAt)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined',
                                        error: formErrors.endAt
                                    },
                                }}
                                label={t('creations.steps.fillInCreationParameters.emissionEnd')}
                                onChange={(event) => {
                                    setFormErrors({...formErrors, endAt: false})
                                    dispatch(setCreationForm({
                                        ...creationForm,
                                        endAt: dayjs(event).date() ? dayjs(event).format() : ''
                                    }))
                                }}/></Box>
                    </Box>
                    {!ability.can('access', 'SimplifiedInterface') && (<><FormControl fullWidth
                                                                                      sx={{marginTop: '25px'}}>
                        <InputLabel shrink id="demo-simple-select-label">Pora dnia</InputLabel>
                        <Select
                            disabled={creationDisabled}
                            variant={"outlined"}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Pora dnia"
                            value={creationForm.timeOfDay || ''}
                            notched
                            onChange={(event) => {
                                dispatch(setCreationForm({
                                    ...creationForm,
                                    timeOfDay: event.target.value
                                }));
                            }}
                        >
                            <MenuItem value="">
                                <em>Brak</em>
                            </MenuItem>
                            {cmEnums && (cmEnums.find(cmEnum => cmEnum.name === 'timeOfDays')?.values as string[]).map((timeOfDay) => (
                                <MenuItem
                                    key={timeOfDay}
                                    value={timeOfDay}>{t(`creations.steps.fillInCreationParameters.timeOfDays.${timeOfDay}`)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                        <FormControl fullWidth sx={{marginTop: '25px'}}>
                            <InputLabel shrink id="demo-simple-select-label">Intensywność</InputLabel>
                            <Select
                                disabled={creationDisabled}
                                variant={"outlined"}
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Intensywność"
                                value={creationForm.intensity || ''}
                                notched
                                onChange={(event) => {
                                    dispatch(setCreationForm({
                                        ...creationForm,
                                        intensity: event.target.value
                                    }));
                                }}
                            >
                                <MenuItem value="">
                                    <em>Brak</em>
                                </MenuItem>
                                {cmEnums && (cmEnums.find(cmEnum => cmEnum.name === 'intensities')?.values as string[]).map((intensity) => (
                                    <MenuItem
                                        key={intensity}
                                        value={intensity}>{t(`creations.steps.fillInCreationParameters.intensities.${intensity}`)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{marginTop: '25px'}}>
                            <InputLabel shrink id="demo-simple-select-label">Pogoda</InputLabel>
                            <Select
                                disabled={creationDisabled}
                                variant={"outlined"}
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Intensywność"
                                value={creationForm.weather || ''}
                                notched
                                onChange={(event) => {
                                    dispatch(setCreationForm({
                                        ...creationForm,
                                        weather: event.target.value
                                    }));
                                }}
                            >
                                <MenuItem value="">
                                    <em>Brak</em>
                                </MenuItem>
                                {cmEnums && (cmEnums.find(cmEnum => cmEnum.name === 'weathers')?.values as string[]).map((weather) => (
                                    <MenuItem
                                        key={weather}
                                        value={weather}>{t(`creations.steps.fillInCreationParameters.weathers.${weather}`)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box sx={{marginTop: '20px'}}><OwnershipSelect
                            ownershipsAttributes={creationForm.ownershipsAttributes}
                            disabled={creationDisabled}
                            onDelete={(val) => {
                                const removingFromDb = creationDetails ? creationDetails.ownerships.find(person => person.userId === parseInt(val)) : undefined
                                dispatch(setCreationForm({
                                    ...creationForm,
                                    ownershipsAttributes: removingFromDb ? [...creationForm.ownershipsAttributes.map((el) => {
                                        if (el.userId === removingFromDb.userId) {
                                            return {...el, _destroy: true}
                                        }
                                        return el
                                    })] : creationForm.ownershipsAttributes.filter(person => person.userId !== parseInt(val))
                                }));
                            }}

                            onAdd={addAuthorizedPerson}
                            onDeleteAll={() => {
                                dispatch(setCreationForm({
                                    ...creationForm,
                                    ownershipsAttributes: []
                                }));
                            }}
                        ></OwnershipSelect></Box></>)}
                </form>
            </Box>

            {!creationDetails && (<Box sx={{width: '20%'}}>
                <Card>
                    <CardContent>
                        <Typography gutterBottom sx={{
                            color: 'text.secondary',
                            fontSize: 14,
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            Notatka
                        </Typography>
                    </CardContent>
                    <CardActions sx={{display: 'flex', justifyContent: 'center'}}>
                        <Button onClick={() => {
                            setOpenNoteModal(true)
                        }} size="small">{creationForm?.commentAttributes?.body ? 'Otwórz' : 'Dodaj'}</Button>
                    </CardActions>
                </Card>
                {noteDialog}
            </Box>)}
        </Box>

    return (
        <>
            <Backdrop
                sx={(theme) => ({color: '#fff', zIndex: theme.zIndex.drawer + 1})}
                open={deletingCreation}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>
            <Box className={'page-header'}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: '20px'}}>
                        <h1>{creationId ? t("creations.editCreation") + " " + (creationDetails ? creationDetails?.name : '') : t("creations.newCreationCreator")}</h1>
                        {(creationId && creationDetails) && (
                            <CmCreationStatusIconBox rejectionReason={creationDetails.rejectionReason}
                                                     showStatusText={true}
                                                     state={creationDetails.state}></CmCreationStatusIconBox>)}
                    </Box>
                    <Box sx={{display: 'flex', gap: '40px'}}>
                        {((draftDetails || creationDetails) && !shouldSkipDraftsStep && ability.can('access', 'Draft')) && (<Link
                            component="button"
                            onClick={() => {
                                navigate(`/drafts/${draftDetails ? draftDetails.id : creationDetails?.draftId}`)
                            }}
                        >
                            Schemat: <b>{draftDetails ? draftDetails.name : creationDetails?.draftName}</b>
                        </Link>)}
                        {creationId && (
                            <Button onClick={() => {
                                setOpenRemoveCreationDialog(true)
                            }} variant={'outlined'}
                                    color={'error'}><Typography> {t('creations.removeCreation')} </Typography></Button>
                        )}</Box>
                </Box>
                {(creationDetails && creationId) &&
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '20px', marginTop: '20px'}}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            width: '100%'
                        }}>
                            <Box>
                                <Box sx={{marginTop: '10px'}}>{actionButtons(creationDetails.possibleEvents)}</Box>
                            </Box>

                            <Card sx={{minWidth: 275}}>
                                <CardContent>
                                    <Typography gutterBottom sx={{
                                        color: 'text.secondary',
                                        fontSize: 14,
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}>
                                        Notatka
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{display: 'flex', justifyContent: 'center'}}>
                                    <Button onClick={() => {
                                        setOpenNoteModal(true)
                                    }}
                                            size="small">{creationForm?.commentAttributes?.body ? 'Otwórz' : 'Dodaj'}</Button>
                                </CardActions>
                            </Card>
                        </Box>
                        {noteDialog}

                    </Box>}
            </Box>
            <Box sx={{width: '100%', paddingTop: '20px'}}>
                <Stepper activeStep={activeStep}>
                    {steps.map((label, index) => {
                        const stepProps: { completed?: boolean } = {};
                        const labelProps: {
                            optional?: React.ReactNode;
                        } = {};
                        if (isStepSkipped(index)) {
                            stepProps.completed = false;
                        }
                        return (
                            <Step key={label} {...stepProps}>
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                <>
                    {activeStep === 0 && (!shouldSkipDraftsStep && (selectDraftStep))}

                    {activeStep === 1 && (adjustCreationViewStep)}

                    {activeStep === 2 && (fillInParametersStep)}

                    <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                        {((activeStep > 0 && !creationId && (!shouldSkipDraftsStep || (activeStep > 1))) || (activeStep > 1 && creationId)) && (
                            <Button
                                color="inherit"
                                disabled={activeStep === 0}
                                onClick={handleBack}
                                sx={{mr: 1}}
                            >
                                {t("common.back")}
                            </Button>)
                        }
                        <Box sx={{flex: '1 1 auto'}}/>
                        {!hideUpdateCreationButton && (<Button onClick={handleNext} disabled={disableNextButton}>
                            {getNextButtonTitle()}
                        </Button>)}
                    </Box>
                </>
            </Box>

            {openRemoveCreationDialog && (<Dialog
                open={openRemoveCreationDialog}
                onClose={() => {
                    setOpenRemoveCreationDialog(false);
                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Usuwanie kreacji
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {'Usunąć kreacje ' + creationDetails?.name + '?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant={'contained'} onClick={() => {
                        handleRemoveCreation()
                    }}>{t('common.remove')}</Button>
                    <Button onClick={() => {
                        setOpenRemoveCreationDialog(false);
                    }} autoFocus>
                        {t('common.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>)}
        </>
    );
};

export default CreationForm;
